import { CHART_COLORS, CHART_VIEW } from "../constants";
import {
  createFrame,
  drawCrosshair,
  drawEntry,
  drawMarker,
  drawPriceAxis,
  getCandleX,
  getLinePointXs,
  getToneColor,
  isTimeBasedLine,
} from "./chartFrame";

const LINE_WIDTH = 2;

/**
 * A short trade: line from the entry price through the candle closes,
 * over an area that fades out downwards. Takes the params of drawCandles and
 * `revealed`: the line reaches that far, between two closes while it plays.
 * A closed trade ends on its exit point in place of the last candle's close,
 * so the replay glides straight into it; with its times known the line spans
 * the plot from entry to exit, each point at its close time.
 *
 * @param {CanvasRenderingContext2D} ctx scaled to CSS pixels
 * @param {object} params see drawCandles
 * @param {number} params.revealed candle points drawn, fractional
 * @param {number|null} params.entryTime open time of the trade, ms
 * @param {number|null} params.exitTime close time of a closed trade, ms
 * @param {number|null} params.intervalMs candle interval, ms
 * @param {{index: number, price: number, label: string}|null} params.hover candle under the pointer
 * @returns {{x: number, y: number}|null} the entry point
 */
export const drawLine = (
  ctx,
  {
    width,
    height,
    candles,
    rangeCandles,
    revealed,
    entryPrice,
    exitPrice,
    entryTime,
    exitTime,
    intervalMs,
    isDone,
    isProfit,
    hover,
    formatPrice,
  },
) => {
  const startPrice = entryPrice ?? rangeCandles[0].open;
  const hasExit = exitPrice != null;
  const lastIndex = rangeCandles.length - 1;
  // The exit point takes the place of the last candle's close
  const closes = (hasExit ? rangeCandles.slice(0, -1) : rangeCandles).map(
    ({ close }) => close,
  );
  const prices = [startPrice, ...closes];

  if (hasExit) prices.push(exitPrice);

  const frame = createFrame({ width, height, prices });
  const { plotWidth, bottom, toY } = frame;
  const slotX = (index) =>
    getCandleX(CHART_VIEW.LINE, index, plotWidth, rangeCandles.length);
  const timeXs = isTimeBasedLine({
    view: CHART_VIEW.LINE,
    exitPrice,
    entryTime,
    exitTime,
    intervalMs,
  })
    ? getLinePointXs({
      rangeCandles,
      intervalMs,
      entryTime,
      exitTime,
      plotWidth,
    })
    : null;
  const toX = (index) => {
    if (index < 0) return 0;

    return timeXs ? timeXs[index] : slotX(index);
  };
  // Entry point first, then one point per candle, the exit for the last one
  const points = [
    [0, toY(startPrice)],
    ...candles.map(({ close }, index) =>
      [toX(index), toY(hasExit && index === lastIndex ? exitPrice : close)],
    ),
  ];
  const stepShare = revealed - Math.floor(revealed);

  // The last segment is drawn as far as the replay got
  if (stepShare > 0 && points.length > 1) {
    const [previousX, previousY] = points[points.length - 2];
    const [nextX, nextY] = points[points.length - 1];

    points[points.length - 1] = [
      previousX + (nextX - previousX) * stepShare,
      previousY + (nextY - previousY) * stepShare,
    ];
  }

  const [lastX, lastY] = points[points.length - 1];
  const color = getToneColor(isProfit);

  drawPriceAxis(ctx, frame, formatPrice);

  const area = ctx.createLinearGradient(0, frame.top, 0, bottom);

  area.addColorStop(0, isProfit ? CHART_COLORS.areaUp : CHART_COLORS.areaDown);
  area.addColorStop(1, CHART_COLORS.areaEnd);

  ctx.beginPath();
  points.forEach(([x, y]) => ctx.lineTo(x, y));
  ctx.lineTo(lastX, bottom);
  ctx.lineTo(0, bottom);
  ctx.closePath();
  ctx.fillStyle = area;
  ctx.fill();

  ctx.beginPath();
  points.forEach(([x, y]) => ctx.lineTo(x, y));
  ctx.strokeStyle = color;
  ctx.lineWidth = LINE_WIDTH;
  ctx.lineJoin = "round";
  ctx.stroke();

  const entryPoint =
    entryPrice != null ? drawEntry(ctx, frame, 0, entryPrice) : null;

  if (hasExit && isDone) drawMarker(ctx, lastX, lastY, color);

  if (hover) {
    drawCrosshair(ctx, frame, {
      x: toX(hover.index),
      price: hover.price,
      label: hover.label,
    });
  }

  return entryPoint;
};
